import React, { useState, useEffect } from 'react';
import './XmlViewer.css';
import './SavedXmlFiles.css'; // Import table styles
import axios from 'axios';
import azureStorageService from '../services/azureStorageService';
import useAuth from '../hooks/useAuth.ts';

const XmlViewer = () => {
  const { auth } = useAuth();
  const [xmlFiles, setXmlFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [xmlContent, setXmlContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');

  // Check if user has permission to delete files (Manager or Editor only)
  const canDeleteFiles = auth.user && (auth.user.role === 'Manager' || auth.user.role === 'Editor');

  // Fetch XML files from Azure Storage
  useEffect(() => {
    const fetchXmlFiles = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch files from Azure Storage
        const files = await azureStorageService.listXmlFiles();
        setXmlFiles(files || []);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching XML files:', err);
        setError('Failed to load XML files. Please try again later.');
        setLoading(false);
      }
    };

    fetchXmlFiles();
  }, []);

  // Fetch XML content when a file is selected
  useEffect(() => {
    if (selectedFile) {
      const fetchXmlContent = async () => {
        try {
          setLoading(true);
          
          // Try to fetch from Azure Storage first
          if (selectedFile.url) {
            const content = await azureStorageService.getXmlFileContent(selectedFile.name);
            if (content) {
              setXmlContent(content);
              setLoading(false);
              return;
            }
          }
          
          // If Azure Storage fails, try the API endpoint
          const response = await axios.get(`/api/xml-files/${selectedFile.id}/content`);
          setXmlContent(response.data.content);
          setLoading(false);
        } catch (err) {
          console.error('Error fetching XML content:', err);
          setError('Failed to load XML content. Please try again later.');
          setLoading(false);
        }
      };

      fetchXmlContent();
    } else {
      setXmlContent('');
    }
  }, [selectedFile]);

  // Filter files based on search term and customer access
  const filteredFiles = xmlFiles.filter(file => {
    // First check if the file matches the search term
    const matchesSearch = 
      file.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (file.metadata?.wellName || file.wellName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (file.metadata?.operator || file.operator || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (file.metadata?.customerName || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  // Handle column header click for sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // If clicking the same field, toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a different field, set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
    
  };

  // Sort files based on current sort field and direction
  const sortedFiles = [...filteredFiles].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortField) {
      case 'name':
        aValue = a.name;
        bValue = b.name;
        break;
      case 'wellName':
        aValue = a.metadata?.wellName || a.wellName || '';
        bValue = b.metadata?.wellName || b.wellName || '';
        break;
      case 'operator':
        aValue = a.metadata?.operator || a.operator || '';
        bValue = b.metadata?.operator || b.operator || '';
        break;
      case 'customer':
        aValue = a.metadata?.customerName || '';
        bValue = b.metadata?.customerName || '';
        break;
      case 'date':
        aValue = new Date(a.date);
        bValue = new Date(b.date);
        break;
      case 'size':
        aValue = a.size;
        bValue = b.size;
        break;
      default:
        return 0;
    }
    
    // Handle string comparison
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      const comparison = aValue.localeCompare(bValue);
      return sortDirection === 'asc' ? comparison : -comparison;
    }
    
    // Handle numeric/date comparison
    if (aValue < bValue) {
      return sortDirection === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortDirection === 'asc' ? 1 : -1;
    }
    return 0;
  });

  // Get sort icon for column headers
  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <span className="sort-icon">↕</span>;
    }
    return sortDirection === 'asc' 
      ? <span className="sort-icon active">↑</span>
      : <span className="sort-icon active">↓</span>;
  };

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  };

  // Format date for display
  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Handle file selection
  const handleFileSelect = (file) => {
    setSelectedFile(file);
    setError(null);
  };

  // Handle file download
  const handleDownload = () => {
    if (!selectedFile || !xmlContent) return;
    
    const blob = new Blob([xmlContent], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = selectedFile.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Handle quick download without loading content for viewing
  const handleQuickDownload = async (file) => {
    try {
      setLoading(true);
      let content;
      
      // Try to fetch from Azure Storage first
      if (file.url) {
        content = await azureStorageService.getXmlFileContent(file.name);
        if (!content) {
          const response = await axios.get(`/api/xml-files/${file.id}/content`);
          content = response.data.content;
        }
      } else {
        const response = await axios.get(`/api/xml-files/${file.id}/content`);
        content = response.data.content;
      }
      
      // Download the content
      const blob = new Blob([content], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setLoading(false);
    } catch (err) {
      console.error('Error downloading file:', err);
      setError('Failed to download file. Please try again later.');
      setLoading(false);
    }
  };
  
  // Handle file deletion
  const handleDelete = async (file) => {
    if (!window.confirm(`Are you sure you want to delete ${file.name}?`)) {
      return;
    }
    
    try {
      setLoading(true);
      await azureStorageService.deleteXmlFile(file.name);
      
      // Update the file list
      setXmlFiles(prevFiles => prevFiles.filter(f => f.id !== file.id && f.name !== file.name));
      
      // If the deleted file was selected, clear the selection
      if (selectedFile && (selectedFile.id === file.id || selectedFile.name === file.name)) {
        setSelectedFile(null);
        setXmlContent('');
      }
      
      alert(`File ${file.name} has been deleted.`);
    } catch (err) {
      console.error('Error deleting file:', err);
      setError(`Failed to delete file: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="xml-viewer-container">
      <div className="xml-viewer-header">
        <h3>XML Files</h3>
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Search files..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      
      <div className="xml-viewer-files" style={{ maxHeight: 'none' }}>
        {loading && sortedFiles.length === 0 ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading files from cloud storage...</p>
          </div>
        ) : sortedFiles.length === 0 ? (
          <div className="no-files">
            {searchTerm ? 'No files match your search.' : 'No XML files found in cloud storage.'}
          </div>
        ) : (
          <table className="files-table">
            <thead>
              <tr>
                <th className="sortable-header" onClick={() => handleSort('name')}>
                  File Name <span style={{float: 'right'}}>{getSortIcon('name')}</span>
                </th>
                <th className="sortable-header" onClick={() => handleSort('wellName')}>
                  Well Name <span style={{float: 'right'}}>{getSortIcon('wellName')}</span>
                </th>
                <th className="sortable-header" onClick={() => handleSort('operator')}>
                  Operator <span style={{float: 'right'}}>{getSortIcon('operator')}</span>
                </th>
                <th className="sortable-header" onClick={() => handleSort('customer')}>
                  Customer <span style={{float: 'right'}}>{getSortIcon('customer')}</span>
                </th>
                <th className="sortable-header" onClick={() => handleSort('date')}>
                  Date <span style={{float: 'right'}}>{getSortIcon('date')}</span>
                </th>
                <th className="sortable-header" onClick={() => handleSort('size')}>
                  Size <span style={{float: 'right'}}>{getSortIcon('size')}</span>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedFiles.map(file => (
                <tr 
                  key={file.id || file.name}
                  className={selectedFile && (selectedFile.id === file.id || selectedFile.name === file.name) ? 'selected-row' : ''}
                >
                  <td onClick={() => handleFileSelect(file)} style={{ cursor: 'pointer' }}>
                    {file.name}
                  </td>
                  <td>{file.metadata?.wellName || file.wellName || 'Unknown Well'}</td>
                  <td>{file.metadata?.operator || file.operator || 'Unknown Operator'}</td>
                  <td>{file.metadata?.customerName || 'N/A'}</td>
                  <td>{formatDate(file.date)}</td>
                  <td>{formatFileSize(file.size)}</td>
                  <td className="file-actions">
                    <button 
                      className="action-button view-button"
                      onClick={() => handleFileSelect(file)}
                      title="View XML"
                    >
                      <i className="fas fa-eye"></i>
                    </button>
                    {file.url && (
                      <button 
                        className="action-button download-button"
                        onClick={() => handleQuickDownload(file)}
                        title="Download"
                      >
                        <i className="fas fa-cloud-download-alt"></i>
                      </button>
                    )}
                    {canDeleteFiles && (
                      <button 
                        className="action-button delete-button"
                        onClick={() => handleDelete(file)}
                        title="Delete XML"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="files-footer">
          {sortedFiles.length > 0 && `${sortedFiles.length} file(s) found in cloud storage`}
        </div>
      </div>
      
      <div className="xml-viewer-content">
        {selectedFile ? (
          <>
            <div className="content-header">
              <h2>{selectedFile.name}</h2>
              <div className="content-actions">
                <button
                  className="cloud-download-button"
                  onClick={handleDownload}
                  disabled={!xmlContent || loading}
                >
                  <i className="fas fa-download"></i> Download
                </button>
              </div>
            </div>
            
            <div className="content-body">
              {loading ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>Loading XML content...</p>
                </div>
              ) : error ? (
                <div className="error-message">{error}</div>
              ) : (
                <pre className="xml-content">{xmlContent}</pre>
              )}
            </div>
          </>
        ) : (
          <div className="no-selection">
            <div className="no-selection-icon">
              <i className="fas fa-file-code"></i>
            </div>
            <h3>No XML file selected</h3>
            <p>Select a file from the list above to view its content</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default XmlViewer;
